import Validator from 'validatorjs';

import type { FormState } from 'core/form';
import type { ArrayFieldState } from 'core/form/types';
import { NETWORK_SCOPE_ANY, type ContextConjunctionValues, type ContextPredicateRow } from 'app/util/contextFilters';
import { hasContextOperand, type ContextOperand } from '@kentik/ui-shared/alerting/contextFilterScope';

export const CONTEXT_DUPLICATE_FIELD_MESSAGE =
  'Each field can be used only once per condition set. Add another condition set to match a different value.';
export const CONTEXT_EMPTY_CLAUSE_MESSAGE = 'Add at least one condition, or remove this condition set.';
export const CONTEXT_EMPTY_CONJUNCTION_MESSAGE = 'Add at least one condition set, or remove this alternative.';

const CONTEXT_STRUCTURAL_MESSAGES: readonly string[] = [
  CONTEXT_DUPLICATE_FIELD_MESSAGE,
  CONTEXT_EMPTY_CLAUSE_MESSAGE,
  CONTEXT_EMPTY_CONJUNCTION_MESSAGE
];

export const getContextStructuralErrors = (errors: string[] = []): string[] =>
  errors.filter((error) => CONTEXT_STRUCTURAL_MESSAGES.includes(error));

Validator.register(
  'contextPredicateValue',
  (value: unknown) => hasContextOperand(value as ContextOperand),
  'A value is required'
);

Validator.register(
  'contextNoDuplicateFields',
  (predicates: unknown) => {
    const fields = (Array.isArray(predicates) ? (predicates as ContextPredicateRow[]) : [])
      .map(({ field }) => field)
      .filter(Boolean);

    return fields.length === new Set(fields).size;
  },
  // Each field has one slot per condition set on the wire, so a duplicate would overwrite its twin.
  CONTEXT_DUPLICATE_FIELD_MESSAGE
);

Validator.register(
  'contextClauseNotEmpty',
  (predicates: unknown) => Array.isArray(predicates) && predicates.length > 0,
  CONTEXT_EMPTY_CLAUSE_MESSAGE
);

Validator.register(
  'contextConjunctionNotEmpty',
  (clauses: unknown) => Array.isArray(clauses) && clauses.length > 0,
  CONTEXT_EMPTY_CONJUNCTION_MESSAGE
);

export interface ContextFilterFieldConfig {
  label?: string;
  placeholder?: string;
  rules?: string | Array<string | Record<string, unknown>>;
  defaultValue?: unknown;
  options?: ReadonlyArray<{ label: string; value: unknown }>;
  messages?: Record<string, string>;
  isComplexArray?: boolean;
}

/**
 * @param fieldName the form field holding the filter, e.g. `context_filter`.
 */
export const CONTEXT_CONTAINER_LABELS = {
  conjunctions: 'Filters',
  clauses: 'Condition Sets',
  predicates: 'Conditions'
} as const;

export function createContextFilterFields(fieldName: string): Record<string, ContextFilterFieldConfig> {
  const conjunctions = `${fieldName}.conjunctions`;
  const clauses = `${conjunctions}[].clauses`;
  const predicates = `${clauses}[].predicates`;

  return {
    [conjunctions]: {
      label: CONTEXT_CONTAINER_LABELS.conjunctions,
      isComplexArray: true,
      defaultValue: []
    },

    [clauses]: {
      label: CONTEXT_CONTAINER_LABELS.clauses,
      isComplexArray: true,
      defaultValue: [],
      rules: ['contextConjunctionNotEmpty']
    },

    [`${clauses}[].network_scope`]: {
      label: 'Applies to',
      defaultValue: [NETWORK_SCOPE_ANY]
    },

    [predicates]: {
      label: CONTEXT_CONTAINER_LABELS.predicates,
      isComplexArray: true,
      defaultValue: [],
      rules: ['contextClauseNotEmpty', 'contextNoDuplicateFields']
    },

    [`${predicates}[].field`]: {
      label: 'Field',
      rules: 'required',
      defaultValue: '',
      messages: { required: 'Select a field' }
    },

    [`${predicates}[].operator`]: {
      label: 'Operator',
      rules: 'required',
      defaultValue: '',
      messages: { required: 'Select an operator' }
    },

    [`${predicates}[].not`]: {
      defaultValue: false
    },

    [`${predicates}[].value`]: {
      label: 'Value',
      rules: ['contextPredicateValue'],
      defaultValue: '',
      placeholder: 'Enter value...',
      messages: { contextPredicateValue: 'Enter a value' }
    }
  };
}

export const getContextConjunctionsField = (
  form: FormState,
  fieldName: string
): ArrayFieldState<ContextConjunctionValues[], ContextConjunctionValues> | null =>
  form.getField(`${fieldName}.conjunctions`) as ArrayFieldState<
    ContextConjunctionValues[],
    ContextConjunctionValues
  > | null;
